import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {  } from 'identity-webapi-client';
import { 
  AddStyleTemplateRequest, AddStyleTemplateResponse, ListStyleTemplatesRequest, ListStyleTemplatesResponse, ProductName, 
  SituViewWebApiClient, StyleConfigTemplate, Company, FileParameter
} from 'situview-webapi-client';
import { RenameDialogComponent } from 'src/app/components/rename-dialog/rename-dialog.component';
import { AuthService, CompanyViewService, StateService, ConfirmDialogService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';

@Component({
  selector: 'style-template-list',
  templateUrl: './style-template-list.component.html',
  styleUrls: ['./style-template-list.component.less']
})
export class StyleTemplateListComponent extends SituBaseComponent {
  loading: boolean = false;
  data: Record<string, Record<string, StyleConfigTemplate[]>> = {};
  searchTerm = "";
  productNames = ProductName;
  productName: ProductName | undefined;
  isHidden: boolean | undefined;
  globalOnly: boolean | undefined;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private snackBar: MatSnackBar,
    private situViewApiClient: SituViewWebApiClient,
    private dialog: MatDialog,
    private companyViewService: CompanyViewService,
    private dialogService: ConfirmDialogService
  ) { 
    super(authService, stateService);
  }

  protected onSelectedCompanyChange(): void {
    this.loadData();
  }

  add() {
    const dialogRef = this.dialog.open(RenameDialogComponent, {
      width: '350px',
      height: '60%',
      data: { 
        existingName: ""
      }
    });

    this._subscriptions.push(dialogRef.afterClosed().subscribe(name => {
        if (name !== undefined) {
          let request = new AddStyleTemplateRequest();
          request.companyId = this.selectedCompany?.id;
          request.name = name;
          request.styles = [];

          this.situViewApiClient.addStyleTemplate(request)
            .then((response: AddStyleTemplateResponse) => {
                if (response.success) {
                    this.snackBar.open(`Style Template ${name} successfully added!`, 'OK', { duration: 2000 });
                    this.router.navigate(['admin/style-template', response.styleTemplateId ]);
                } else {
                    this.snackBar.open(`Error adding Style Template ${name}!`, 'OK', { duration: 2000 });
                }
            })
            .catch((err: any) => {
                this.snackBar.open(`Error adding Style Template ${name}!`, 'OK', { duration: 2000 });
            });
        }
    }));
  }

  onClickStyleTemplate(item: StyleConfigTemplate, home: string | undefined, tag: string | undefined) {
    if (home) {
      this.router.navigate(['admin/style-template', item.id ], { queryParams: { home: home }});
    } else if (tag) {
      this.router.navigate(['admin/style-template', item.id ], { queryParams: { tag: tag }});
    } else {
      this.router.navigate(['admin/style-template', item.id ]);
    }
  }

  setProductName(productName: ProductName) {
    this.productName = productName;
    this.loadData();
  }
  
  setIsHidden(isHidden: boolean) {
    this.isHidden = isHidden;
    this.loadData();
  }
  
  setGlobalOnly(globalOnly: boolean) {
    this.globalOnly = globalOnly;
    this.loadData();
  }

  loadData() {
    this.loading = true;

    let request = new ListStyleTemplatesRequest();
    request.companyId = this.selectedCompany?.id;
    if (this.searchTerm) request.templateName = this.searchTerm;
    if (this.productName !== undefined) request.productName = this.productName;
    if (this.isHidden !== undefined) request.isHidden = this.isHidden;
    if (this.globalOnly !== undefined) request.globalOnly = this.globalOnly;
    request.showAll = true;

    this.situViewApiClient.listStyleTemplates(request)
      .then((response: ListStyleTemplatesResponse) => {
        if (response.success) {
          // Group the style templates by name
          const groupedData = this.groupByPrecedence(response.styleTemplates ?? []);
          //this.data.data = groupedData;
          this.data = groupedData;
        } else {
          this.showMessage("There was an issue retrieving style templates!");
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
        this.showMessage("Could not retrieve style templates!");
      });
  }

  groupByPrecedence(templates: StyleConfigTemplate[]): Record<string, Record<string, StyleConfigTemplate[]>> {
    return templates.reduce<Record<string, Record<string, StyleConfigTemplate[]>>>((acc, template) => {
      const groupName = template.name || 'Unknown';
      const precedence = template.homeId ? 'home' : template.tagId ? 'tag' : 'global';
      acc[groupName] = acc[groupName] || { home: [], tag: [], global: [] };
      acc[groupName][precedence].push(template);
      return acc;
    }, {});
  }
  
  objectKeys(obj: Record<string, Record<string, StyleConfigTemplate[]>>): string[] {
    return Object.keys(obj);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  uploadCSV() {
    if (!this.selectedCompany?.code) {
      this.showMessage("Please select a company first.");
      return;
    }

    // Show confirmation dialog before proceeding
    this.dialogService.open({
      title: 'Import Style Templates',
      message: `Are you sure you want to import style templates for ${this.selectedCompany.code}? This will create new style templates based on the CSV data.`,
      cancelText: 'Cancel',
      confirmText: 'Import Style Templates',
      actionIcon: 'file_upload',
      actionColour: 'primary'
    } as ConfirmDialogData);

    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.proceedWithCSVUpload();
        }
      }));
  }

  private proceedWithCSVUpload() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.style.display = 'none';
    
    fileInput.onchange = (event: any) => {
      const file = event.target.files[0];
      if (!file) return;
      
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (fileExt !== 'csv') {
        this.showMessage("Only CSV files are allowed");
        return;
      }
      
      this.loading = true;
      
      // Create FileParameter object for the API
      const fileParameter = {
        data: file,
        fileName: file.name
      };
      
      this.situViewApiClient.importStyleTemplatesFromCsv(fileParameter, this.selectedCompany?.id)
        .then((response: any) => {
          this.loading = false;
          let message = `Import completed! `;
          message += `Processed: ${response.totalProcessed || 0}, `;
          message += `Created: ${response.templatesCreated || 0}, `;
          message += `Updated: ${response.templatesUpdated || 0}`;
          
          this.showMessage(message);
          this.loadData();
        })
        .catch((error: any) => {
          this.loading = false;
          this.showMessage("Error importing CSV file!");
        });
    };
    
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  }


}
